// MyAttendancePage.jsx — 직원 본인 출퇴근 wrapper page (Step 4 신규)
//
// 목적: AttendanceSection을 별도 라우트(/my/attendance)에서 렌더하기 위한 wrapper
// 회귀 zero 원칙: useDashboardBase/useDashboardActions를 그대로 호출 (Dashboard.jsx와 동일 데이터)
//   - Step 7에서 useDashboardBase 분할/정리 시 useMyAttendance hook으로 추출 가능
//   - 현재는 모든 데이터 fetch (admin이 /my/attendance 진입 시에도) — 정상 동작 보장
//
// 작성: 2026-04-27, dashboard-layout-migration Step 4
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import AttendanceSection from '../../features/attendance/AttendanceSection';
import { DashboardModals } from '../dashboard/DashboardModals';
import { useDashboardBase } from '../dashboard/useDashboardBase';
import { useDashboardActions } from '../dashboard/useDashboardActions';
import { fmtDate, weekdayOne, fmtTime, getAttendanceSummary } from '../dashboard/utils';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobile } from '../../hooks/useIsMobile';
import { stdHeaderRow, stdPageTitle, stdPageSubtitle } from '../admin/adminlayout/designStandard';

export default function MyAttendancePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const base = useDashboardBase({ navigate, showToast });
  const actions = useDashboardActions(base, { navigate, showToast });

  // 권한 가드 — canUseMyAttendance가 false면 접근 차단 (사이드바엔 메뉴 미노출이지만 직접 URL 접근 대응)
  const permissions = usePermissions({
    permCodes: base.permCodesSet,
    taskCodes: base.taskCodes,
    featureSetNames: base.featureSetNames,
    workerType: base.me?.workerType,
    externalCompany: base.me?.externalCompany,
  });

  // 연도/월 변경 시 재조회 (Dashboard.jsx의 useEffect와 동일)
  useEffect(() => {
    if (!permissions.canUseMyAttendance) return;
    base.fetchAttendanceRecords(base.attendanceYear, base.attendanceMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base.attendanceYear, base.attendanceMonth, permissions.canUseMyAttendance]);

  // 기록이 있는 연도만 한 번 받아 둔다 (연도 셀렉트용)
  useEffect(() => {
    if (!permissions.canUseMyAttendance) return;
    base.fetchAttendanceYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.canUseMyAttendance]);

  // 권한 없을 시 fallback (메뉴엔 안 보이지만 URL 직접 접근 가능)
  if (base.permissionsLoaded && !permissions.canUseMyAttendance) {
    return <div style={{ padding: 24, color: 'var(--text-muted)' }}>출퇴근 권한이 없습니다.</div>;
  }

  // 관리자가 직원을 열람 중인가 — 사이드바 드릴다운이 ?as_user_id 를 붙인다.
  //   ⚠ base.me 는 **열람 대상이 아니라 관리자 본인**이다. /api/me 는 사이드바가 쓰는 값이라
  //     apiClient 의 DRILL_DOWN_EXCLUDE(apiClient.js:118)에 들어 있어 as_user_id 가 안 붙는다.
  //     그래서 여기에 base.me.name 을 쓰면 누구를 골라도 "관리자 님" 으로 뜬다(2026-09-14 신고).
  //     대상 이름을 쓰려면 별도 조회가 필요해, 지금은 이름을 쓰지 않는다.
  //     누구를 보는지는 왼쪽 사이드바의 직원 선택 드롭다운이 알려준다.
  const viewingOther = new URLSearchParams(location.search).has('as_user_id');

  // 제목 영역은 출퇴근 관리와 같은 양식(designStandard)을 쓴다 (2026-09-14).
  const pageTitle = viewingOther ? '출퇴근 열람' : '내 출퇴근';
  const pageSubtitle = viewingOther
    ? '선택한 직원의 출퇴근 기록입니다.'
    : '출퇴근을 기록하고 내 기록을 확인합니다.';

  return (
    <>
      {/* ── 페이지 헤더: 제목/부제 — 출퇴근 관리와 같은 양식 ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div style={{ minWidth: 0 }}>
          <h2 style={stdPageTitle}>{pageTitle}</h2>
          <div style={stdPageSubtitle}>{pageSubtitle}</div>
        </div>
      </div>

      {/* 지각 사유 입력 모달 — 출퇴근 페이지 전용 (lateReasonModalOpen은 useDashboardBase 내부 상태) */}
      <DashboardModals
        lateReasonModalOpen={base.lateReasonModalOpen}
        latePromptInfo={base.latePromptInfo}
        actionLoading={base.actionLoading}
        lateReasonText={base.lateReasonText}
        setLateReasonText={base.setLateReasonText}
        setLateReasonModalOpen={base.setLateReasonModalOpen}
        submitLateReasonCheckIn={actions.submitLateReasonCheckIn}
        // 비밀번호 변경 모달은 PasswordChangeModalGlobal(AppLayout)이 처리 — 여기선 noop props
        pwModal={false} forcePwChange={false} pwForm={{}} setPwForm={() => {}} changePassword={() => {}} setPwModal={() => {}}
      />
      {/* 본인 출퇴근 섹션 — Dashboard.jsx 와 동일 props (회귀 zero) */}
      <AttendanceSection
      today={base.today}
      ipAllowed={base.ipAllowed} ipCheckInfo={base.ipCheckInfo} myIp={base.myIp}
      locationUsable={base.locationUsable} addressLoading={base.addressLoading} currentAddress={base.currentAddress}
      actionLoading={base.actionLoading}
      attendanceList={base.attendanceList} attendanceLoading={base.attendanceLoading}
      attendanceYear={base.attendanceYear} attendanceMonth={base.attendanceMonth}
      yearOptions={base.yearOptions} monthOptions={base.monthOptions}
      /* 상단 카드 + 연도 셀렉트 (2026-09-14 시안) */
      profile={base.attendanceProfile} availableYears={base.attendanceYears}
      brandLogoUrl={base.me?.tenant?.logoUrl || null}
      brandName={base.me?.tenant?.displayName || base.me?.tenant?.name || base.me?.company?.name || ''}
      registeredIpLabel={base.ipCheckInfo.expected_ip?.label || ''}
      expandedAttendance={base.expandedAttendance}
      breakDraftById={base.breakDraftById} savingBreakById={base.savingBreakById}
      fmtDate={fmtDate} weekdayOne={weekdayOne} fmtTime={fmtTime}
      getAttendanceSummary={getAttendanceSummary}
      onCheckIn={actions.doCheckIn} onCheckOut={actions.doCheckOut}
      onRequestLocation={actions.requestCurrentLocation}
      onYearChange={base.setAttendanceYear} onMonthChange={base.setAttendanceMonth}
      onToggleDetail={(key) => base.setExpandedAttendance((prev) => ({ ...prev, [key]: !prev[key] }))}
      onBreakDraftChange={(id, val) => base.setBreakDraftById((prev) => ({ ...prev, [id]: val }))}
      onSaveBreak={actions.saveBreakMinutes}
    />
    </>
  );
}
